import { generateSearchQuery } from '@app/utils/generateSearchQuery/generateSearchQuery'
import { Review } from '@core/domain/models/review.model'
import { ArticleMetadata } from '@core/domain/models/reviewItemMetadata/articleMetadata'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import axios from 'axios'

export class CochraneApiClient {
  async createReviewSearch(review: Review): Promise<{
    query?: string
    filters?: string
    date: string
    reviewId: number
    importSourceId: string
    items: ArticleMetadata & { type: ReviewItemType.Article }[]
    queryTranslation?: string
    url?: string
  }> {
    const query = generateSearchQuery(BuiltInImportSourceId.COCHRANE, review)
    const result = await this.search(query)
    return {
      query,
      date: new Date().toISOString(),
      reviewId: review.id,
      importSourceId: BuiltInImportSourceId.COCHRANE,
      items: result.map((a) => ({
        ...a,
        type: ReviewItemType.Article,
      })),
    }
  }
  private async search(query: string): Promise<ArticleMetadata[]> {
    let articles: ArticleMetadata[] = []
    const url = '/live-scraper/cochrane'
    const { parsedQuery, startDate, endDate } = this.parseQuery(query)
    try {
      const response = await axios.post(
        url + `?query=${parsedQuery}&startDate=${startDate}&endDate=${endDate}`,
      )
      articles = response.data.map((data: any) => this.mapToArticle(data))
    } catch (error) {
      console.error('Error fetching records from Cochrane API:', error)
      throw new Error(`Error fetching records from Cochrane API`)
    }

    return articles
  }

  private parseQuery(query: string): {
    parsedQuery: string
    startDate: string
    endDate: string
  } {
    const dateRangeRegex =
      /Date range: \((\d{2}\/\d{2}\/\d{4}) to (\d{2}\/\d{2}\/\d{4})\)/
    const queryRegex = /^(.*?) in Record Title/

    const dateMatch = dateRangeRegex.exec(query)
    const queryMatch = queryRegex.exec(query)

    if (!dateMatch || !queryMatch) {
      throw new Error('Invalid query format: missing query or date range')
    }

    const startDate = dateMatch[1]
    const endDate = dateMatch[2]
    const parsedQuery = queryMatch[1]

    return {
      parsedQuery,
      startDate,
      endDate,
    }
  }

  private mapToArticle(data: any): ArticleMetadata {
    return new ArticleMetadata({
      abstract: data.abstract,
      authors: data.authors,
      title: data.title,
      url: data.url,
      doi: data.doi,
      issueNumber: data.issueNumber,
      journal: data.journal,
      pagesNumber: data.pagesNumber,
      pmid: data.pmid,
      publishYear: data.publishYear,
      rawData: data.rawData,
      volumeNumber: data.volumeNumber,
    })
  }
}
